import { FunctionComponent } from 'react';
import { graphql, useFragment } from 'react-relay';
import { useAppDispatch } from '../../reducers/root';
import { updateStripePaymentMethodApiType } from '../../actions/checkoutPaymentActions';
import { METHODS } from '../../constants/checkoutConstants';
import { PaymentElement } from '@stripe/react-stripe-js';
import { StripePaymentElementOptions, StripePaymentElementChangeEvent } from '@stripe/stripe-js';
import { CheckoutStripeForm_transaction$key } from './__generated__/CheckoutStripeForm_transaction.graphql';

type CheckoutStripeFormProps = {
    transaction: CheckoutStripeForm_transaction$key;
};

export const CheckoutStripeForm: FunctionComponent<CheckoutStripeFormProps> = ({
    transaction: transactionRef,
}) => {
    const transaction = useFragment(
        graphql`
            fragment CheckoutStripeForm_transaction on Transaction {
                shipment {
                    shippingAddress {
                        country
                        zipCode
                        displayName
                    }
                }
            }
        `,
        transactionRef
    );

    const dispatch = useAppDispatch();

    const shippingAddress = transaction?.shipment?.shippingAddress;

    const paymentElementOptions: StripePaymentElementOptions = {
        layout: {
            type: 'tabs',
            defaultCollapsed: false,
            radios: true,
            spacedAccordionItems: false,
        },
        wallets: {
            applePay: 'auto',
            googlePay: 'auto',
        },
        //this controls whether or not stripe shows disclaimer captions in the stripe form. Right now, we don't want ito show these
        terms: {
            auBecsDebit: 'never',
            bancontact: 'never',
            card: 'never',
            ideal: 'never',
            sepaDebit: 'never',
            usBankAccount: 'never',
        },
        //controls which personal info input fields stripe shows to the buyer
        fields: {
            billingDetails: {
                email: 'never',
                address: {
                    postalCode: 'never',
                    country: 'never',
                },
            },
        },
        defaultValues: {
            billingDetails: {
                name: shippingAddress?.displayName || '',
                address: {
                    country: shippingAddress?.country || '',
                    postal_code: shippingAddress?.zipCode || '',
                },
            },
        },
    };

    //resolve the 1stDibs payment type from the stripe payment type
    const getSelectedPaymentTab = function (event: StripePaymentElementChangeEvent): string | null {
        switch (event?.value?.type) {
            case 'us_bank_account':
                return METHODS.METHOD_ACH;
            case 'card':
                return METHODS.METHOD_CREDIT_CARD;
            default:
                return null;
        }
    };

    //we need to get the selected payment method type from the stripe element and save it to redux for checkout submission
    const onChange = function (event: StripePaymentElementChangeEvent): void {
        const resolvedValue = getSelectedPaymentTab(event);
        dispatch(updateStripePaymentMethodApiType(resolvedValue));
    };

    return (
        <PaymentElement id="payment-element" options={paymentElementOptions} onChange={onChange} />
    );
};

export default CheckoutStripeForm;
